/**
 * Alle Einstellungen des Roboters an einem Ort: was es gibt, wie man es
 * aendert, was voreingestellt ist und was man dabei wissen muss.
 *
 * Die API-Hilfe darunter beschreibt jeden Aufruf einzeln, aber verstreut ueber
 * die Klassen. Wer wissen will "was kann ich am Roboter ueberhaupt
 * einstellen?", fand dort keine Antwort.
 *
 * **Die Werte sind nicht abgeschrieben, sondern angebunden.** Jede Vorgabe, die
 * aus einer Konstanten der Firmware stammt, nennt sie unter ``quelle``.
 * roboter-einstellungen.test.ts liest die Python-Dateien und prueft, dass die
 * Zahl hier noch stimmt, und dass jeder genannte Aufruf in der API existiert.
 * Aendert sich die Firmware, faellt die Seite im Prueflauf auf und nicht erst
 * im Unterricht.
 */

/** Wann eine Einstellung geaendert werden darf. */
export type Zeitpunkt =
  | "jederzeit"
  | "stillstand"
  | "vor-dem-start"
  | "blockiert";

export interface Quelle {
  /** Pfad ab ``python/robot/``. */
  datei: string;
  /** Name der Konstanten auf Modulebene. */
  konstante: string;
  /** Wert, wie er in der Datei steht. */
  wert: string;
}

export interface Einstellung {
  name: string;
  /** Beispielaufruf, direkt in ein Programm uebernehmbar. */
  aufruf: string;
  /** Voreinstellung in Worten, mit Einheit. */
  vorgabe: string;
  /** Erlaubte Werte. */
  bereich?: string;
  wann: Zeitpunkt;
  wirkung: string;
  /** Was man wissen muss, bevor man es aendert. */
  hinweis?: string;
  quelle?: Quelle[];
}

export interface FesterWert {
  name: string;
  wert: string;
  bedeutung: string;
  quelle?: Quelle[];
}

export interface Gruppe {
  id: string;
  titel: string;
  einleitung: string;
  /** Aufruf, mit dem man die aktuellen Werte liest. */
  ablesen?: string;
  einstellungen: Einstellung[];
  /** Nicht einstellbar, aber wichtig zu kennen. */
  fest?: FesterWert[];
}

export const ZEITPUNKT_TEXT: Record<Zeitpunkt, string> = {
  jederzeit: "jederzeit, auch während der Fahrt",
  stillstand: "nur im Stillstand – sonst RobotBusyError",
  "vor-dem-start": "einmal, am Programmanfang",
  blockiert: "blockiert das Programm, bis sie fertig ist",
};

/** Allgemeines, das fuer alle Gruppen gilt. */
export const GRUNDSAETZE: string[] = [
  "Einstellungen gelten vom Aufruf bis zum Ende des Programms. Nach jedem Start – auch nach dem Senden und nach dem Einschalten – gelten wieder die Voreinstellungen. Deshalb gehören sie ins Programm, gleich nach robot.init().",
  "Wird ein Wert beim Aufruf weggelassen, bleibt er unverändert. drive.configure(max_speed_m_s=0.6) ändert nur die Höchstgeschwindigkeit.",
  "Alle Werte sind SI-Einheiten: Meter, Sekunden, Radiant. Die Endung im Namen sagt es: _m_s ist m/s, _rad_s ist rad/s, _s sind Sekunden.",
  "„Nur im Stillstand“ heißt: solange ein Fahr- oder Motorbefehl den Antrieb belegt, antwortet der Aufruf mit RobotBusyError. Vorher robot.stop() oder den Befehl beenden.",
  "Ungültige Werte werden nicht still angepasst, sondern mit ValueError abgelehnt. Die Fehlermeldung nennt den erlaubten Bereich.",
];

export const GRUPPEN: Gruppe[] = [
  {
    id: "start",
    titel: "Start und Pinbelegung",
    einleitung:
      "robot.init() prüft die Verkabelung, startet die Hardware und misst die Batterie. Ohne diesen Aufruf sind alle anderen Einstellungen wirkungslos. Die Motoren bleiben dabei aus.",
    ablesen: "robot.info()",
    einstellungen: [
      {
        name: "Pinbelegung",
        aufruf:
          'robot.init(pins={\n    "motors": {"left": (6, 7), "right": (8, 9), "mode": 10},\n    "encoders": {"left": (12, 13), "right": (14, 15)},\n    "imu": {"sda": 4, "scl": 5, "int": 1},\n    "ultrasonic": {"trigger": 27, "echo": 0},\n    "buttons": {"left": 2, "right": 3},\n    "led": 11,\n    "battery_adc": 28,\n})',
        vorgabe: "keine – die Angabe ist Pflicht",
        bereich:
          "GPIO 0–22 und 26–28. battery_adc nur 26, 27 oder 28 (Analogeingänge). imu.int darf fehlen; die Firmware benutzt den Pin nicht, er ist nur als belegt vermerkt. Jeder Pin höchstens einmal.",
        wann: "vor-dem-start",
        wirkung:
          "Legt fest, an welchen Anschlüssen des Pico Motoren, Encoder, Lagesensor, Ultraschall, Taster, LED und Batteriemessung hängen.",
        hinweis:
          "GPIO 23, 24, 25 und 29 gehören dem Funkmodul und der Versorgung des Boards und sind gesperrt. Eine fehlende, doppelte oder unzulässige Angabe bricht mit PinError ab – und die Meldung nennt die Stelle.",
      },
    ],
    fest: [
      {
        name: "Batteriemessung beim Start",
        wert: "25 Messungen, je 2 ms Abstand",
        bedeutung:
          "robot.init() blockiert dafür rund 50 ms. Schlägt die Messung fehl, bricht init mit RobotFaultError ab – ohne gültige Spannung bleiben die Motoren gesperrt.",
        quelle: [{ datei: "_battery.py", konstante: "SAMPLE_INTERVAL_US", wert: "2000" }],
      },
    ],
  },
  {
    id: "fahrwerk",
    titel: "Fahrwerk",
    einleitung:
      "Grenzen und Verhalten für drive.velocity(), drive.straight(), drive.turn() und alle anderen Fahrbefehle. Überschreitet ein Befehl eine Grenze, lehnt der Roboter ihn mit ValueError ab – er fährt nicht einfach langsamer.",
    ablesen: "drive.state()",
    einstellungen: [
      {
        name: "Höchstgeschwindigkeit",
        aufruf: "drive.configure(max_speed_m_s=0.4)",
        vorgabe: "0,4 m/s",
        bereich: "größer als 0",
        wann: "stillstand",
        wirkung:
          "Fahrbefehle mit mehr Geschwindigkeit werden abgelehnt: drive.velocity(0.5, 0) wirft ValueError und beendet das Programm, wenn es den Fehler nicht abfängt.",
        hinweis:
          "Die 0,4 m/s sind ein Platzhalter und wurden nie am Roboter bestimmt. Sie sollen verhindern, dass ein Tippfehler wie drive.velocity(4, 0) den Roboter losschießen lässt. Beim Joystick darauf achten: y * 0.5 ergibt bei vollem Ausschlag 0,5 m/s und liegt darüber.",
        quelle: [{ datei: "_drive.py", konstante: "DEFAULT_MAX_SPEED_M_S", wert: "0.4" }],
      },
      {
        name: "Höchste Drehrate",
        aufruf: "drive.configure(max_turn_rad_s=3.0)",
        vorgabe: "3,0 rad/s (rund 172 °/s)",
        bereich: "größer als 0",
        wann: "stillstand",
        wirkung: "Drehbefehle über dieser Rate werden mit ValueError abgelehnt.",
        hinweis: "Wie die Höchstgeschwindigkeit ein nie bestimmter Platzhalter.",
        quelle: [{ datei: "_drive.py", konstante: "DEFAULT_MAX_TURN_RAD_S", wert: "3.0" }],
      },
      {
        name: "Beschleunigung",
        aufruf: "drive.configure(acceleration_m_s2=0.5)",
        vorgabe: "keine Rampe – der Sollwert springt",
        bereich: "größer als 0",
        wann: "stillstand",
        wirkung:
          "Begrenzt, wie schnell sich die Sollgeschwindigkeit ändern darf. Der Roboter fährt weicher an und bremst weicher ab.",
        hinweis:
          "Ohne Rampe ruckt der Roboter beim Anfahren, und ein balancierender Aufbau kann dabei kippen.",
      },
      {
        name: "Verhalten am Ende eines Fahrauftrags",
        aufruf: 'drive.configure(stop_mode="brake")',
        vorgabe: '"coast" – ausrollen',
        bereich: '"coast" ausrollen, "brake" bremsen, "hold" Position halten',
        wann: "stillstand",
        wirkung:
          "Was die Motoren tun, wenn drive.straight() oder drive.turn() ihr Ziel erreicht haben.",
        hinweis:
          "\"hold\" regelt weiter und verbraucht dabei Strom. Für den Rundumstopp ist robot.stop() da, nicht diese Einstellung.",
      },
      {
        name: "Zeitgrenze für Fahraufträge",
        aufruf: "drive.configure(timeout_s=30.0)",
        vorgabe: "20 s",
        bereich: "größer als 0",
        wann: "stillstand",
        wirkung:
          "Ein Auftrag mit Ziel – Strecke, Winkel, Zeit – der sein Ziel in dieser Zeit nicht erreicht, wird abgebrochen und meldet das.",
        hinweis:
          "Gilt nicht für Dauerbefehle wie drive.velocity() oder motor_left.run(): die laufen, bis ein anderer Befehl kommt.",
        quelle: [{ datei: "_motion.py", konstante: "DEFAULT_TIMEOUT_S", wert: "20.0" }],
      },
    ],
    fest: [
      {
        name: "Raddurchmesser",
        wert: "90 mm",
        bedeutung:
          "Rechnet Radumdrehungen in Meter um. Vorläufiger Wert, am Roboter noch zu bestätigen – stimmt er nicht, stimmen Strecken und Geschwindigkeiten nicht.",
        quelle: [{ datei: "_profile.py", konstante: "WHEEL_DIAMETER_M", wert: "0.09" }],
      },
      {
        name: "Spurweite",
        wert: "110 mm",
        bedeutung:
          "Abstand der Räder; bestimmt, wie weit sich der Roboter bei einer Drehung dreht. Vorläufiger Wert, am Roboter noch zu bestätigen.",
        quelle: [{ datei: "_profile.py", konstante: "TRACK_WIDTH_M", wert: "0.11" }],
      },
    ],
  },
  {
    id: "motoren",
    titel: "Motoren und Drehzahlregler",
    einleitung:
      "Jeder Motor hat zwei Regler: einen für die Drehzahl (motor_left.run(), alle Fahrbefehle) und einen für den Winkel (motor_left.run_to_angle(), motor_left.run_for_angle()). Einstellungen gelten je Motor – für beide Seiten also zweimal aufrufen.",
    ablesen: "motor_left.settings()",
    einstellungen: [
      {
        name: "Drehzahlregler",
        aufruf: 'motor_left.set_pid("speed", 6.0, 12.0, 0.0)\nmotor_right.set_pid("speed", 6.0, 12.0, 0.0)',
        vorgabe: "kp 6,0 · ki 12,0 · kd 0,0",
        bereich: 'loop "speed" oder "position"; Beiwerte beliebige Zahlen',
        wann: "jederzeit",
        wirkung:
          "Wie kräftig der Regler auf eine Abweichung der Drehzahl reagiert. kp sofort, ki über die Zeit, kd auf die Änderung.",
        hinweis:
          "Die Vorgabe ist ein Platzhalter; die Regler sind nicht eingestellt. Am Roboter bewährt haben sich kp = 8 und ki = 40. Während der Fahrt ändern ist ausdrücklich erlaubt – der aufsummierte I-Anteil bleibt dabei erhalten, damit die Drehzahl nicht springt. Gut mit einem Schieberegler aus remote.number() zu verbinden.",
        quelle: [{ datei: "_motion.py", konstante: "DEFAULT_SPEED_PID", wert: "(6.0, 12.0, 0.0)" }],
      },
      {
        name: "Winkelregler",
        aufruf: 'motor_left.set_pid("position", 4.0, 0.0, 0.2)',
        vorgabe: "kp 4,0 · ki 0,0 · kd 0,2",
        wann: "jederzeit",
        wirkung: "Wie der Motor einen Zielwinkel anfährt und hält.",
        quelle: [
          { datei: "_motion.py", konstante: "DEFAULT_POSITION_PID", wert: "(4.0, 0.0, 0.2)" },
        ],
      },
      {
        name: "Höchster Tastgrad",
        aufruf: "motor_left.configure(max_pwm=80)",
        vorgabe: "100 %",
        bereich: "über 0 bis 100",
        wann: "stillstand",
        wirkung: "Deckelt die Ansteuerung des Motors in Prozent.",
        hinweis:
          "Unabhängig davon gilt immer die 6-V-Grenze (siehe Sicherheit): bei vollem Akku sind 100 % hier nie 100 % am Motor.",
      },
      {
        name: "Beschleunigung",
        aufruf: "motor_left.configure(acceleration_rad_s2=20.0)",
        vorgabe: "keine Rampe",
        bereich: "größer als 0",
        wann: "stillstand",
        wirkung: "Begrenzt, wie schnell sich die Solldrehzahl ändern darf, in rad/s².",
      },
      {
        name: "Verhalten am Ende eines Auftrags",
        aufruf: 'motor_left.configure(stop_mode="hold")',
        vorgabe: '"coast" – ausrollen',
        bereich: '"coast", "brake", "hold"',
        wann: "stillstand",
        wirkung: "Was der Motor tut, wenn ein Auftrag mit Ziel fertig ist.",
      },
      {
        name: "Zeitgrenze für Aufträge",
        aufruf: "motor_left.configure(timeout_s=30.0)",
        vorgabe: "20 s",
        bereich: "größer als 0",
        wann: "stillstand",
        wirkung: "Bricht einen Auftrag mit Ziel ab, der es in dieser Zeit nicht erreicht.",
        hinweis: "Gilt nicht für motor_left.run() – ein Dauerbefehl hat kein Ziel.",
        quelle: [{ datei: "_motion.py", konstante: "DEFAULT_TIMEOUT_S", wert: "20.0" }],
      },
      {
        name: "Blockadeerkennung",
        aufruf: "motor_left.configure(stall_s=1.0)",
        vorgabe: "0,5 s",
        bereich: "größer als 0",
        wann: "stillstand",
        wirkung:
          "Steht das Rad so lange, obwohl der Regler mindestens 25 % Tastgrad anfordert, gilt der Antrieb als blockiert: der Regler schaltet ab, der Auftrag endet, und der Motor hält nach seinem stop_mode an.",
        hinweis:
          "Die ersten 1,5 s nach dem Anfahren wird nicht geprüft – Anfahren gegen die Haftreibung sieht sonst genauso aus wie eine Blockade. Mit sehr schwachen Reglerbeiwerten kann ein Motor fälschlich als blockiert gelten.",
        quelle: [
          { datei: "_motion.py", konstante: "DEFAULT_STALL_S", wert: "0.5" },
          { datei: "_motion.py", konstante: "STALL_PWM", wert: "25.0" },
          { datei: "_motion.py", konstante: "STALL_GRACE_S", wert: "1.5" },
        ],
      },
    ],
    fest: [
      {
        name: "PWM-Frequenz",
        wert: "500 Hz",
        bedeutung: "Frequenz, mit der die Motoren angesteuert werden.",
        quelle: [{ datei: "_motor.py", konstante: "PWM_FREQUENCY_HZ", wert: "500" }],
      },
    ],
  },
  {
    id: "regler",
    titel: "Eigener Regler",
    einleitung:
      "control.start() lässt eine eigene Funktion auf dem Regelkern laufen, in festem Takt und unabhängig vom Programm. control.PID ist ein fertiger Regler mit Begrenzung und Anti-Windup.",
    ablesen: "control.timing()",
    einstellungen: [
      {
        name: "Regelintervall",
        aufruf: "control.start(mein_regler, period_s=0.01)",
        vorgabe: "keine – die Angabe ist Pflicht",
        bereich: "0,002 s bis 1,0 s",
        wann: "vor-dem-start",
        wirkung: "Wie oft die eigene Funktion aufgerufen wird.",
        hinweis:
          "Schneller als der Grundtakt von 2 ms geht nicht. control.timing() zeigt, ob der Takt gehalten wird – dort zuerst nachsehen, wenn ein Regler sich merkwürdig verhält.",
        quelle: [
          { datei: "control.py", konstante: "MAX_PERIOD_S", wert: "1.0" },
          { datei: "_supervisor.py", konstante: "TICK_US", wert: "2000" },
        ],
      },
      {
        name: "Erlaubte Laufzeit je Aufruf",
        aufruf: "control.start(mein_regler, period_s=0.01, deadline_s=0.004)",
        vorgabe: "die halbe Periode",
        bereich: "über 0 bis period_s",
        wann: "vor-dem-start",
        wirkung: "Dauert ein Aufruf länger, wird das als verpasster Termin gezählt.",
        quelle: [{ datei: "control.py", konstante: "DEFAULT_DEADLINE_FRACTION", wert: "0.5" }],
      },
      {
        name: "PID-Regler",
        aufruf: "pid = control.PID(kp=2.0, ki=0.5, kd=0.0, limits=(-100, 100))",
        vorgabe: "keine – Beiwerte beim Anlegen",
        bereich: "limits als (unten, oben); reverse=True kehrt das Vorzeichen um",
        wann: "jederzeit",
        wirkung:
          "Rechnet mit pid.update(soll, ist, dt_s) einen Stellwert. pid.kp, pid.ki und pid.kd lassen sich jederzeit direkt ändern.",
      },
    ],
  },
  {
    id: "lage",
    titel: "Lagesensor (IMU)",
    einleitung:
      "Der Lagesensor misst Beschleunigung und Drehrate. Ein Filter rechnet daraus die Neigung – und mit Mahony oder Madgwick die ganze Lage im Raum.",
    ablesen: "imu.sample()",
    einstellungen: [
      {
        name: "Nullpunkte bestimmen",
        aufruf: "imu.calibrate()",
        vorgabe: "3,0 s Messdauer",
        bereich: "duration_s größer als 0",
        wann: "blockiert",
        wirkung:
          "Misst die Drehraten im Stillstand und zieht sie künftig ab. Ohne Kalibrierung laufen die Winkel langsam weg.",
        hinweis:
          "Der Roboter muss dabei still stehen. Bewegt er sich, bricht die Messung mit RobotFaultError ab. Am Gerät gemessen: ohne Kalibrierung zeigten Komplementärfilter und Mahony in Ruhe 0,26° Abweichung, danach 0,06°.",
        quelle: [{ datei: "_imu.py", konstante: "MOTION_LIMIT_RAD_S", wert: "0.15" }],
      },
      {
        name: "Filter wählen",
        aufruf: 'imu.configure_filter(mode="mahony")',
        vorgabe: '"complementary"',
        bereich: '"complementary", "mahony", "madgwick"',
        wann: "jederzeit",
        wirkung:
          "Komplementärfilter: nur die Neigung, am billigsten. Mahony und Madgwick: die ganze Lage als Quaternion – erst dann liefern imu.quaternion(), imu.orientation() und telemetry.send_orientation() etwas.",
        hinweis:
          "Ein Wechsel setzt die Neigung zurück; imu.tilt() ist einen Messschritt lang None. Rechenzeit je Lesung am Gerät: Mahony 230 µs, Madgwick 301 µs.",
      },
      {
        name: "Zeitkonstante (Komplementärfilter)",
        aufruf: "imu.configure_filter(time_constant_s=0.5)",
        vorgabe: "0,5 s",
        bereich: "größer als 0",
        wann: "jederzeit",
        wirkung:
          "Darunter zählt die Drehrate, darüber die Beschleunigung. Klein: folgt schnell, aber unruhig. Groß: ruhig, aber träge.",
        quelle: [{ datei: "_imu.py", konstante: "DEFAULT_TIME_CONSTANT_S", wert: "0.5" }],
      },
      {
        name: "Gewicht der Drehrate (Komplementärfilter)",
        aufruf: "imu.configure_filter(gain=0.98)",
        vorgabe: "aus der Zeitkonstante berechnet",
        bereich: "0 bis 1",
        wann: "jederzeit",
        wirkung:
          "Setzt das Gewicht unmittelbar statt über die Zeitkonstante. 0 heißt nur Beschleunigung, 1 nur Drehrate – dann driftet der Winkel ungebremst weg.",
        hinweis: "Eine neue Zeitkonstante setzt ein zuvor gesetztes gain zurück.",
      },
      {
        name: "Korrektur kp und ki (Mahony)",
        aufruf: "imu.configure_filter(kp=2.0, ki=0.0)",
        vorgabe: "kp 2,0 · ki 0,0",
        bereich: "nicht negativ",
        wann: "jederzeit",
        wirkung:
          "kp: wie stark die Beschleunigung die Lage nachführt; 1/kp ist die Zeitkonstante. ki: schätzt den Nullpunkt der Drehrate während der Fahrt mit, 0 schaltet das ab.",
        quelle: [
          { datei: "_orientation.py", konstante: "DEFAULT_KP", wert: "2.0" },
          { datei: "_orientation.py", konstante: "DEFAULT_KI", wert: "0.0" },
        ],
      },
      {
        name: "Schrittweite beta (Madgwick)",
        aufruf: "imu.configure_filter(beta=0.1)",
        vorgabe: "0,1",
        bereich: "nicht negativ",
        wann: "jederzeit",
        wirkung:
          "Madgwick korrigiert jeden Fehler mit höchstens rund 2 · beta rad/s, gleich wie groß er ist – keine Zeitkonstante wie bei Mahony.",
        quelle: [{ datei: "_orientation.py", konstante: "DEFAULT_BETA", wert: "0.1" }],
      },
      {
        name: "Tiefpass im Sensor",
        aufruf: "imu.configure_filter(lowpass_hz=21)",
        vorgabe: "44 Hz",
        bereich: "0 (aus), 5, 10, 21, 44, 94 oder 184",
        wann: "jederzeit",
        wirkung:
          "Glättet Beschleunigung und Drehrate schon im Sensor. Niedriger: ruhiger, aber verzögert.",
        hinweis:
          "Der neue Wert wird nicht sofort geschrieben, sondern vom Regelkern im nächsten Takt – ein Schreiben vom Programm aus hielt den Regelkern gemessen bis zu 51 ms an.",
        quelle: [{ datei: "_imu.py", konstante: "DEFAULT_LOWPASS_HZ", wert: "44" }],
      },
      {
        name: "Kalibrierung und Bezugswinkel von Hand",
        aufruf: "imu.configure_calibration(tilt_reference_rad=0.05)",
        vorgabe: "keine Nullpunkte, Skalen 1, Bezug 0",
        bereich: "gyro_offset (rad/s), accel_offset (m/s²), accel_scale (Faktor), je drei Werte",
        wann: "jederzeit",
        wirkung:
          "Setzt Nullpunkte und Skalen ohne Messung, etwa gespeicherte Werte einer früheren Kalibrierung. tilt_reference_rad ist der Winkel, den imu.tilt() als 0 ausgibt.",
      },
    ],
    fest: [
      {
        name: "Lesetakt",
        wert: "alle 4 ms (250 Hz)",
        bedeutung: "So oft liest der Regelkern den Sensor und rechnet den Filter.",
        quelle: [{ datei: "_imu.py", konstante: "READ_INTERVAL_US", wert: "4000" }],
      },
    ],
  },
  {
    id: "odometrie",
    titel: "Odometrie",
    einleitung:
      "Die Odometrie rechnet aus den Radumdrehungen, wo der Roboter steht und wohin er schaut.",
    ablesen: "odometry.pose_encoder()",
    einstellungen: [
      {
        name: "Pose setzen",
        aufruf: "odometry.reset(x_m=0.0, y_m=0.0, heading_rad=0.0)",
        vorgabe: "(0, 0, 0) beim Start",
        wann: "stillstand",
        wirkung: "Legt fest, wo der Roboter jetzt steht und wohin er zeigt.",
        hinweis:
          "Die Pose läuft mit der Zeit weg, vor allem die Richtung: Radschlupf und die vorläufigen Werte für Raddurchmesser und Spurweite summieren sich.",
      },
    ],
    fest: [
      {
        name: "Encoder",
        wert: "1440 Zählschritte je Radumdrehung",
        bedeutung: "Auflösung der Wegmessung: rund 0,2 mm Weg je Schritt.",
        quelle: [{ datei: "_profile.py", konstante: "COUNTS_PER_WHEEL_TURN", wert: "1440" }],
      },
    ],
  },
  {
    id: "telemetrie",
    titel: "Telemetrie",
    einleitung:
      "Messwerte an die Oberfläche: als Kurven, zum Aufzeichnen als CSV und als Lage im Raum.",
    ablesen: "telemetry.stats()",
    einstellungen: [
      {
        name: "Standardsatz regelmäßig senden",
        aufruf: "telemetry.start(period_s=0.1)",
        vorgabe: "aus; beim Einschalten 0,1 s",
        bereich: "größer als 0",
        wann: "jederzeit",
        wirkung:
          "Sendet Batterie, Neigung, Abstand, Raddrehzahlen und Pose regelmäßig. telemetry.stop() beendet das.",
        hinweis:
          "Mehr als 50 Zeilen je Sekunde gehen nie hinaus; kürzere Perioden sammeln die Werte nur. Jede Zeile kostet den Roboter knapp 1 ms Rechenzeit.",
        quelle: [{ datei: "_telemetry.py", konstante: "MIN_INTERVAL_S", wert: "0.02" }],
      },
      {
        name: "Lage anzeigen",
        aufruf: "telemetry.send_orientation(plot=True, view=True)",
        vorgabe: "Kurven und Darstellung",
        bereich: "plot und view je True oder False",
        wann: "jederzeit",
        wirkung:
          "plot zeigt die vier Werte als Kurven, view den Roboter im Raum. Beides False: nur in der Aufzeichnung.",
        hinweis:
          "Braucht mode=\"mahony\" oder \"madgwick\". Mit dem Komplementärfilter liefert es False und sendet nichts – dann bleibt die Darstellung leer.",
      },
    ],
  },
  {
    id: "rueckkanal",
    titel: "Bedienelemente aus der Oberfläche",
    einleitung:
      "remote.number(), remote.switch(), remote.button() und remote.joystick() melden Bedienelemente in der Oberfläche an und liefern ihre Werte.",
    ablesen: "remote.is_connected()",
    einstellungen: [
      {
        name: "Frist bei Verbindungsabriss",
        aufruf: "remote.timeout(2.0)",
        vorgabe: "2,0 s",
        bereich: "größer als 0, oder None zum Abschalten",
        wann: "jederzeit",
        wirkung:
          "Meldet sich die Oberfläche so lange nicht, fallen alle Bedienelemente auf ihre Vorgabe: ein Joystick in die Ruhelage, Schieberegler und Schalter auf ihren Anfangswert, anstehende Tastendrücke verfallen.",
        hinweis:
          "Die Frist ersetzt nicht den Stopp. Ob der Roboter anhält, entscheidet das Programm, das den Joystickwert weiterreicht. remote.timeout() ohne Argument liefert den eingestellten Wert.",
        quelle: [{ datei: "_remote.py", konstante: "DEFAULT_SESSION_TIMEOUT_S", wert: "2.0" }],
      },
    ],
    fest: [
      {
        name: "Abfrage der Werte",
        wert: "höchstens alle 10 ms, in robot.sleep()",
        bedeutung:
          "Neue Werte kommen nur in robot.sleep() an. Eine Schleife mit robot.sleep(0.05) liest also höchstens 20-mal je Sekunde.",
        quelle: [{ datei: "_remote.py", konstante: "POLL_INTERVAL_US", wert: "10000" }],
      },
    ],
  },
  {
    id: "funk",
    titel: "Bluetooth",
    einleitung:
      "Über Bluetooth verbindet sich die Oberfläche ohne Kabel: Programme senden, Konsole, Telemetrie, Bedienelemente.",
    ablesen: "ble.info()",
    einstellungen: [
      {
        name: "Funk einschalten",
        aufruf: 'ble.start("Robot-07")',
        vorgabe: "aus",
        bereich: "Name mit 1 bis 29 Zeichen",
        wann: "vor-dem-start",
        wirkung:
          "Der Roboter wirbt unter diesem Namen und ist aus der Oberfläche erreichbar. Ab dann gehen alle Ausgaben zusätzlich über Funk. ble.stop() schaltet ab.",
        hinweis:
          "Ohne ble.start() im Programm ist der Roboter nach dem Senden über Funk nicht wieder erreichbar – jeder Neustart schaltet den Funk ab. Deshalb früh im Programm aufrufen, gleich nach robot.init(). Der Stopp-Knopf der Oberfläche wirkt über Funk nur, wenn das Programm robot.sleep() aufruft.",
      },
    ],
  },
  {
    id: "wlan",
    titel: "WLAN",
    einleitung: "Für ROS 2 braucht der Roboter ein Netz – ein vorhandenes oder ein eigenes.",
    ablesen: "wifi.info()",
    einstellungen: [
      {
        name: "Mit einem Netz verbinden",
        aufruf: 'wifi.connect("Netzname", "Passwort")',
        vorgabe: "15 s Zeitgrenze",
        bereich: "nur WPA2-PSK",
        wann: "blockiert",
        wirkung: "Klinkt sich in ein vorhandenes Netz ein und liefert die eigene IP-Adresse.",
        hinweis:
          "Ein Hochschulnetz wie eduroam geht nicht – dafür ist das eigene Netz da.",
        quelle: [{ datei: "_wifi.py", konstante: "DEFAULT_TIMEOUT_S", wert: "15.0" }],
      },
      {
        name: "Eigenes Netz aufmachen",
        aufruf: 'wifi.access_point("robot-07", "geheim123", channel=6)',
        vorgabe: "Kanal 6",
        bereich: "Passwort mindestens 8 Zeichen, oder None für ein offenes Netz",
        wann: "blockiert",
        wirkung: "Der Roboter spannt ein eigenes WLAN auf; der Rechner verbindet sich damit.",
        quelle: [{ datei: "_wifi.py", konstante: "MIN_KEY_LENGTH", wert: "8" }],
      },
    ],
  },
  {
    id: "ros",
    titel: "ROS 2",
    einleitung: "Meldet den Roboter in einem ROS-2-Netz an; braucht ein laufendes WLAN.",
    ablesen: "ros.info()",
    einstellungen: [
      {
        name: "Anmelden",
        aufruf: 'ros.start("robot_07", rate_hz=20)',
        vorgabe: "20 Hz, Port 9001, Bakenport 9000",
        bereich: "rate_hz 1 bis 50; Name aus Kleinbuchstaben, Ziffern, Unterstrich, nicht mit einer Ziffer beginnend",
        wann: "vor-dem-start",
        wirkung: "Der Name wird zum Namensraum der Topics; rate_hz ist die Senderate der Messwerte.",
        quelle: [
          { datei: "_ros.py", konstante: "MIN_RATE_HZ", wert: "1" },
          { datei: "_ros.py", konstante: "MAX_RATE_HZ", wert: "50" },
        ],
      },
      {
        name: "Frist für Fahrbefehle",
        aufruf: "ros.command_timeout(0.5)",
        vorgabe: "0,5 s",
        wann: "jederzeit",
        wirkung: "Kommt so lange kein /cmd_vel, wird die Fahrt genullt.",
        quelle: [{ datei: "_ros.py", konstante: "DEFAULT_COMMAND_TIMEOUT_S", wert: "0.5" }],
      },
      {
        name: "Frist für die Gegenstelle",
        aufruf: "ros.session_timeout(5.0)",
        vorgabe: "5,0 s",
        wann: "jederzeit",
        wirkung: "Nach dieser Zeit ohne Nachricht wird ein stummer Rechner vergessen.",
        quelle: [{ datei: "_ros.py", konstante: "DEFAULT_SESSION_TIMEOUT_S", wert: "5.0" }],
      },
    ],
  },
  {
    id: "sicherheit",
    titel: "Sicherheit – nicht einstellbar",
    einleitung:
      "Diese Werte schützen Roboter und Menschen und lassen sich aus dem Programm nicht ändern. Man sollte sie kennen, weil sie erklären, warum ein Motor nicht anläuft oder der Roboter plötzlich steht.",
    ablesen: "robot.status()",
    einstellungen: [],
    fest: [
      {
        name: "Lokaler Stopp",
        wert: "beide Taster gleichzeitig",
        quelle: [{ datei: "_supervisor.py", konstante: "DEBOUNCE_US", wert: "20000" }],
        bedeutung:
          "Hält alle Motoren sofort an und beendet laufende Aufträge und eigene Regler – im Regelkern, unabhängig vom Programm, vom Browser und von der Funkverbindung. Loslassen allein startet nichts neu. Der Weg aus jeder Störung.",
      },
      {
        name: "Motorspannung",
        wert: "höchstens 6,0 V",
        bedeutung:
          "Der Tastgrad wird aus der Akkuspannung so begrenzt, dass nie mehr als 6 V am Motor anliegen. battery.duty_limit() zeigt die aktuelle Grenze.",
        quelle: [{ datei: "_profile.py", konstante: "MOTOR_VOLTAGE_LIMIT_V", wert: "6.0" }],
      },
      {
        name: "Motorsperre ohne Batteriemessung",
        wert: "unter 4,0 V gilt die Messung als ungültig",
        bedeutung:
          "Ohne gültige Messung – kein Akku, nur USB – bleiben die Motoren gesperrt. Ein unplausibler Wert darf nicht wie ein schwacher Akku zu vollem Tastgrad führen.",
        quelle: [{ datei: "_battery.py", konstante: "PLAUSIBLE_MIN_V", wert: "4.0" }],
      },
      {
        name: "Schwache Batterie",
        wert: "unter 5,0 V",
        bedeutung: "Ab hier meldet battery.is_low() eine schwache Batterie.",
        quelle: [{ datei: "_battery.py", konstante: "LOW_VOLTAGE_V", wert: "5.0" }],
      },
      {
        name: "Grundtakt des Regelkerns",
        wert: "2 ms, Regelschritt alle 4 ms",
        bedeutung:
          "Der zweite Prozessorkern regelt die Motoren, liest Sensoren und überwacht den lokalen Stopp in diesem Takt – auch wenn das Programm hängt.",
        quelle: [
          { datei: "_supervisor.py", konstante: "TICK_US", wert: "2000" },
          { datei: "_supervisor.py", konstante: "CONTROL_PERIOD_US", wert: "4000" },
        ],
      },
      {
        name: "Ultraschall",
        wert: "2 cm bis 4 m, alle 60 ms",
        bedeutung:
          "Außerhalb dieses Bereichs oder ohne Echo liefert distance.read() None – nie eine erfundene Zahl. Ein Messwert gilt höchstens 250 ms.",
        quelle: [
          { datei: "_distance.py", konstante: "MIN_RANGE_M", wert: "0.02" },
          { datei: "_distance.py", konstante: "MAX_RANGE_M", wert: "4.0" },
          { datei: "_distance.py", konstante: "MEASUREMENT_INTERVAL_US", wert: "60000" },
          { datei: "_distance.py", konstante: "MAX_AGE_US", wert: "250000" },
        ],
      },
    ],
  },
];
